import { useState, useEffect } from "react";
import { flights } from "@/data/flights";
import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { QuoteRequestDialog } from "@/components/common/QuoteRequestDialog";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Plane, ArrowRight, Clock, ArrowRightLeft } from "lucide-react";

export default function FlightsPage() {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleQuote = (flight) => {
    setSelectedFlight({
      type: "flight",
      from: flight.from,
      to: flight.to,
      airline: flight.airline,
      price: `$${flight.price}`,
    });
    setQuoteDialogOpen(true);
  };

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <SeoHead
        title="Flight Deals"
        description="Find great international flight deals with Tripsyfy. We compare routes, airlines, and dates with personalized human support."
        canonical="https://tripsyfy.com/flights"
      />
      <Navbar />

      {/* Page header */}
      <section className="relative pt-28 pb-14 bg-primary" data-testid="flights-header">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Great Deals</p>
          <h1 className="heading-xl text-white mb-3">Flight Deals</h1>
          <p className="text-base text-white/70 max-w-lg leading-relaxed">
            Find the best flight deals to your dream destinations. Get a personalized quote and save big on every booking.
          </p>
        </div>
      </section>

      {/* Flights grid */}
      <section className="section-padding bg-surface-subtle" data-testid="flights-grid">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {flights.map((flight, i) => (
              <AnimatedSection key={flight.id} delay={Math.min(i * 0.06, 0.3)}>
                <div className="group bg-white rounded-xl shadow-card hover:shadow-card-hover transition-shadow duration-200 p-5 flex flex-col h-full">
                  {/* Airline header */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-xl bg-surface-soft flex items-center justify-center">
                        <Plane className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-primary">{flight.airline}</p>
                        <p className="text-xs text-muted-foreground">{flight.cabinClass}</p>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-muted-foreground bg-secondary px-3 py-1 rounded-pill">
                      {flight.tripType}
                    </span>
                  </div>

                  {/* Route */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-0.5">From</p>
                      <p className="text-sm font-semibold text-primary">{flight.from}</p>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <ArrowRightLeft className="h-4 w-4 text-accent" />
                    </div>
                    <div className="flex-1 text-right">
                      <p className="text-xs text-muted-foreground mb-0.5">To</p>
                      <p className="text-sm font-semibold text-primary">{flight.to}</p>
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-5">
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> {flight.duration}
                    </span>
                    <span className="text-border">·</span>
                    <span>{flight.stops}</span>
                  </div>

                  {/* Price + CTA */}
                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/60">
                    <div>
                      <p className="text-xs text-muted-foreground">From</p>
                      <p className="text-2xl font-bold text-primary">${flight.price}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleQuote(flight)}
                      className="bg-accent text-white hover:bg-accent/90 gap-1.5"
                    >
                      Get Quote <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <QuoteRequestDialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen} quoteData={selectedFlight} />
    </div>
  );
}
